import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { TouchlessPRBreakdown } from "../../../types/contractDashboard";
import styles from "./touchlessPRRate.module.scss";

interface TouchlessPRRateProps {
  data: TouchlessPRBreakdown[];
}

// Color palette for different PR types
const COLORS = {
  touchless: "#10b981",
  manual: "#f59e0b",
  partial: "#ef4444",
  default: "#ef4444",
} as const;

// Map labels to colors
const getColorForLabel = (label: string): string => {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("touchless") || lowerLabel.includes("auto")) {
    return COLORS.touchless;
  }
  if (lowerLabel.includes("manual")) {
    return COLORS.manual;
  }
  if (lowerLabel.includes("partial")) {
    return COLORS.partial;
  }
  return COLORS.default;
};

// Custom tooltip component
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0];
  const value = data.value as number;
  const name = data.name as string;

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "6px", color: "#1a1a1a" }}
      >
        {name}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: data.payload.fill,
          }}
        />
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Percentage:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
        >
          {value}%
        </Typography>
      </Box>
    </Box>
  );
};

const TouchlessPRRate: React.FC<TouchlessPRRateProps> = ({ data }) => {
  // Memoize chart data transformation and find touchless rate
  const { chartData, touchlessRate } = useMemo(() => {
    const processedData = data.map((item) => ({
      name: item.label,
      value: item.valuePct,
      color: getColorForLabel(item.label),
    }));

    // Find the touchless item (highest priority or first matching)
    const touchlessItem = data.find((item) => {
      const lowerLabel = item.label.toLowerCase();
      return lowerLabel.includes("touchless") || lowerLabel.includes("auto");
    });

    const touchlessPct = touchlessItem ? touchlessItem.valuePct : 0;

    return { chartData: processedData, touchlessRate: touchlessPct };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Touchless PR Rate
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            % of PRs auto-approved and auto-converted to PO.
          </Typography>
        </Box>
        <Box className={styles.chartContainer}>
          <Typography
            variant="body2"
            style={{ textAlign: "center", padding: "20px" }}
          >
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Touchless PR Rate
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          % of PRs auto-approved and auto-converted to PO.
        </Typography>
      </Box>

      <Box
        className={styles.chartContainer}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0",
        }}
      >
        {/* Donut Chart */}
        <Box sx={{ position: "relative", width: "100%", height: "280px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={125}
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
                animationDuration={800}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
                allowEscapeViewBox={{ x: true, y: true }}
                offset={40}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Text */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "#111827",
                fontWeight: 700,
                lineHeight: 1,
                fontSize: "48px",
              }}
            >
              {touchlessRate}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                fontSize: "14px",
                mt: 1.5,
                fontWeight: 500,
              }}
            >
              Touchless PRs
            </Typography>
          </Box>
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 2,
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {chartData.map((item, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                sx={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#374151", fontSize: "13px", fontWeight: 500 }}
              >
                {item.name}{" "}
                <Box
                  component="span"
                  sx={{ color: "#6b7280", fontWeight: 400 }}
                >
                  ({item.value}%)
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(TouchlessPRRate);
